import React, { useEffect, useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';
import FoodImg from '../../common/images/food.png';
import { bleachedSilk, cerebralGrey, timeout } from '../../utils/constants';

const loaded = new Set();
const failedAt = new Map();
const FAIL_TTL = 30000;

const preloadImage = (src, onResult) => {
	const img = new Image();
	let cancelled = false;

	const timer = setTimeout(() => {
		if (!cancelled) onResult('fallback');
	}, timeout);

	img.onload = async () => {
		clearTimeout(timer);
		try {
			if (img.decode) await img.decode();
		} catch (e) {
      console.log('e: ', e);
		}
		if (cancelled) return;
		loaded.add(src);
		onResult('ok');
	};
	img.onerror = () => {
		clearTimeout(timer);
		if (cancelled) return;
		failedAt.set(src, Date.now());
		onResult('fallback');
	};
	img.src = src;

	return () => {
		cancelled = true;
		clearTimeout(timer);
		img.onload = null;
		img.onerror = null;
	};
};

const ImageLoader = (props) => {
	const { src = '', alt = '', className = 'h-full w-full rounded-xl object-cover object-center', containerClassName, priority = false } = props;
	const [isNearViewport, setIsNearViewport] = useState(priority);
	const [result, setResult] = useState({ src: null, status: 'loading' });
	const containerRef = useRef(null);

	useEffect(() => {
		if (priority) return;
		if (!containerRef.current || isNearViewport) return;

		if (typeof IntersectionObserver === 'undefined') {
			setIsNearViewport(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setIsNearViewport(true);
					observer.disconnect();
				}
			},
			{ rootMargin: '200px' },
		);

		observer.observe(containerRef.current);

		return () => observer.disconnect();
	}, [isNearViewport, priority]);

	let status;
	if (!src) status = 'fallback';
	else if (loaded.has(src)) status = 'ok';
	else if (result.src === src) status = result.status;
	else status = 'loading';

	useEffect(() => {
		if (!isNearViewport || !src) return;
		if (loaded.has(src)) return;

		const failed = failedAt.get(src);
		if (failed && Date.now() - failed < FAIL_TTL) {
			setResult({ src, status: 'fallback' });
			return;
		}

		return preloadImage(src, (next) => setResult({ src, status: next }));
	}, [src, isNearViewport]);

	const handleImgError = () => {
		if (status !== 'ok') return;
		loaded.delete(src);
		failedAt.set(src, Date.now());
		setResult({ src, status: 'fallback' });
	};

	return (
		<div ref={containerRef} className={containerClassName ?? className}>
			{status === 'loading' ? (
				<ContentLoader speed={2} width='100%' height='100%' backgroundColor={bleachedSilk} foregroundColor={cerebralGrey} className={className}>
					<rect x='0' y='0' rx='10' ry='10' width='100%' height='100%' />
				</ContentLoader>
			) : (
				<img alt={alt} src={status === 'ok' ? src : FoodImg} className={className} loading='eager' decoding='async' fetchPriority={priority ? 'high' : 'auto'} onError={handleImgError} />
			)}
		</div>
	);
};

export default React.memo(ImageLoader);
